import clsx from "clsx";
import { Link } from "wouter";
import { ChevronDown, ChevronRight, CircleCheck, ExternalLink, History, Plus, Target, Trash2, TriangleAlert } from "lucide-react";
import { type ReactNode, type SyntheticEvent, useEffect, useRef, useState } from "react";
import { basisLabel, toCents, toNumber } from "../../shared/money";
import { ChoiceField, DraftFields, TextField } from "../../web/components/fields";
import { type SyncOption, SyncSelect } from "../../web/components/SyncSelect";
import { SyncBadge } from "../../web/components/SyncBadge";
import { useConfirm } from "../../web/components/Confirm";
import { FixHere } from "../../web/components/FixHere";
import { Badge, Box, DraftBar, ErrorNotice, Notice, SectionCard, StableLabel, type Tone } from "../../web/components/ui";
import { timeAgo } from "../../web/lib/format";
import { useMoney } from "../../web/lib/money";
import { can, type CostDraft, draftPartCost, itemCostShown, itemSupplierPrices, itemTax, priceQuote, quoteRate, shownCost, usePriceHistory, usePriceListSaver, withCostSource } from "./data";
import { ChangeLines } from "./PriceLock";
import { amountCheck as costCheck } from "./PricingEditor";
import { type CostSource, meta, MONEY_PATTERN, type MaterialRow, type PriceListData, type SupplierPriceRow, type SupplierRow, type TaxQuote } from "./meta";
import { costWithLabour, labourChanged, labourCostOf, missingCost, partItem, samePrice } from "./pricing";
import type { ItemDraft } from "./itemDrafts";
import { ProfitBox } from "./ProfitBox";
import { QuotedAmountField, quoteKey, quoteLabel } from "./QuotedAmount";
import { duration } from "./describe";
import { usePriceListWindows } from "./windows";
import { KitParts, PartOfKits } from "./KitParts";
import { WindowPopup } from "../../web/components/WindowPopup";
import { ageOf, bigJump, type CostPick, hasAmount, itemCost, type PriceAge, quotedPrice, type RankedPrice, realCost, storedPrice, usualQuote } from "./supplierPricing";

/*
 * An item's cost (its window's Costing tab: id "suppliers") and what it's costed from (its Suppliers tab, or a kit's Parts: id
 * "sources"), one component (its drafts shared: `show` says which): where its cost comes
 * from (typed in, the cheapest supplier price or one chosen for it: see supplierPricing.ts
 * `costPrice`), the cost itself (typed here, or the price it follows), then each
 * supplier's price as it's quoted (with or without tax, at a rate: as the supplier usually quotes,
 * unless changed for it), from a minimum quantity, with their code and a link, and how
 * long since each was checked.
 */

/** How long since a price was checked, as a badge (here and on a supplier's Prices tab). */
export const AGE: Record<PriceAge, { label: string; tone: Tone }> = {
    current: { label: "Up to date", tone: "green" },
    recheck: { label: "Check due", tone: "amber" },
    outdated: { label: "Out of date", tone: "red" },
};

/** A price as typed (spaces round it don't matter): an amount, or blank (not known yet: it records where to get it). */
const amountCheck = (typed: string) => {
    const v = typed.trim();
    return v === "" || (MONEY_PATTERN.test(v) && Number(v) >= 0) ? null : "Enter an amount, like 12.50, or leave it blank";
};
/** As stored: before tax, or "" when it has no amount yet. */
const toStoredPrice = (typed: string, quote: Pick<TaxQuote, "includesTax">, tax: number) => (typed.trim() === "" ? "" : storedPrice(typed, quote, tax));
const qtyCheck = (v: string) => (Number(v) > 0 ? null : "Enter a quantity more than 0");

/** A price as it's quoted (with or without tax, at its rate), or "" without an amount. */
function quotedOf(data: PriceListData, price: SupplierPriceRow, supplier: SupplierRow | undefined) {
    if (!hasAmount(price)) return "";
    const { quote, tax } = priceQuote(data, price, supplier);
    return quotedPrice(price.priceNet, quote, tax);
}

/**
 * Where its cost comes from, a button each (as its Pricing card's ways of pricing), in full on hover. A supplier price: the
 * cheapest, or one chosen (CostSource "cheapest" / "price"), picked beside the cost (Which price).
 */
type CostFrom = "typed" | "supplier" | "parts";
const COST_FROM: { value: CostFrom; label: string; title: string }[] = [
    { value: "typed", label: "Typed in", title: "Typed in: what you pay for one" },
    { value: "supplier", label: "Supplier price", title: "A supplier price: the cheapest, or one you choose" },
    { value: "parts", label: "Its parts", title: "Its parts' costs added up (a kit)" },
];
const fromOf = (source: CostSource): CostFrom => (source.from === "cheapest" || source.from === "price" ? "supplier" : source.from);

/**
 * A supplier price being added, not saved yet: kept by its window (like a pricing draft), so it stays
 * while another tab is open, and closing asks first (when anything's typed: priceDraftUnsaved).
 */
export interface PriceDraft {
    supplierUuid: string;
    price: string;
    minQty: string;
    code: string;
    url: string;
    /** How the price is quoted, if changed from the supplier's usual. */
    picked: TaxQuote | null;
}
const NEW_PRICE: PriceDraft = { supplierUuid: "", price: "", minQty: "1", code: "", url: "", picked: null };

/** Anything typed in it (a supplier alone, or nothing, isn't worth asking about). */
export const priceDraftUnsaved = (d: PriceDraft | null) => d !== null && (d.price.trim() !== "" || d.code.trim() !== "" || d.url.trim() !== "" || d.minQty.trim() !== "1");

/** "Which price": its cheapest supplier price, whichever it is. */
const CHEAPEST = "cheapest";

export function SupplierPricesTab({
    data,
    item,
    costFocus,
    priceDraft,
    onPriceDraft,
    costFix,
    costFixToken,
    pricesFixToken,
    costDraft,
    onCostDraft,
    show,
    onCostTab,
    pricingDraft,
}: Readonly<{
    /** Which tab: "costing" (its Cost card, and what it makes per unit), or "sources" (what it's costed from: its supplier prices, or a kit's parts). */
    show: "costing" | "sources";
    /** Its pricing being edited (its Pricing tab's draft): what it makes is shown with it. */
    pricingDraft: ItemDraft | null;
    /** Open its Costing tab (a supplier price used for its cost: the draft's there). */
    onCostTab?: () => void;
    data: PriceListData;
    item: MaterialRow;
    costFocus?: number;
    /** Its cost being changed, not saved yet (null: none), kept by the window: saved by its Cost card's save bar. */
    costDraft: CostDraft | null;
    onCostDraft: (draft: CostDraft | null) => void;
    /** The price being added (null: none), kept by the window. */
    priceDraft: PriceDraft | null;
    onPriceDraft: (draft: PriceDraft | null) => void;
    /** Its cost's warning (Cost to update), along the bottom of its Cost section, and Show's token. */
    costFix?: ReactNode;
    costFixToken?: number;
    /** Its supplier prices to check (Needs attention): Show's token, its Supplier prices card centred and flashed. */
    pricesFixToken?: number;
}>) {
    const suppliers = new Map(data.suppliers.map((s) => [s.uuid, s]));
    const { ranked, source, cost } = itemSupplierPrices(data, item.uuid);
    // The price whose details are open (one at a time), and whether a price is being added.
    const [openId, setOpenId] = useState<string | null>(null);
    const adding = priceDraft !== null;
    const setAdding = (on: boolean) => onPriceDraft(on ? NEW_PRICE : null);
    const showAdd = can(data, "prices.add") && !adding;
    const fmt = useMoney();
    // Its Cost card shows the draft, if any (where it would come from, what it would be); the rest of the tab, what's saved.
    const view = costDraft ? withCostSource(data, item.uuid, costDraft.source) : data;
    const shown = itemSupplierPrices(view, item.uuid);
    const savedShown = itemCostShown(data, item);
    const following = fromOf(shown.source) === "supplier";
    // Something typed in its Cost card that can't be taken: its save bar waits (CostSave).
    const [typedBad, setTypedBad] = useState(false);
    // Its cost as the draft would make it (once saved and updated).
    const draftedCost = costDraft ? costWithLabour(data, item, draftPartCost(data, item, costDraft)) : undefined;
    // A change back to what's saved is no change: the draft goes.
    const setDraft = (next: CostDraft) => {
        const sameSource = JSON.stringify(next.source) === JSON.stringify(source);
        const sameTyped = !next.typed || (samePrice(next.typed.amount, savedShown.amount) && quoteKey(next.typed.quote, data.taxRates) === quoteKey(savedShown.quote, data.taxRates));
        onCostDraft(sameSource && sameTyped ? null : next);
    };
    // Following a supplier price: which (the cheapest, else one chosen: here, or from its row below).
    const which = (value: string) => setDraft({ source: value === CHEAPEST ? { from: "cheapest" } : { from: "price", priceId: value } });
    // Those with an amount (one without can't give a cost), cheapest first, by supplier: what it costs, and its minimum.
    const whichOptions: SyncOption[] = [
        { value: CHEAPEST, label: "Cheapest (whichever it is)", synced: false, notRecord: true },
        ...ranked
            .filter((r) => hasAmount(r.price))
            .map((r) => {
                const supplier = suppliers.get(r.price.supplierUuid);
                const min = r.price.minQty > 1 ? ` · min ${fmt.number(r.price.minQty)}` : "";
                return { value: r.price.id, label: supplier?.name ?? "A supplier", synced: supplier?.synced ?? false, detail: `${fmt.money(quotedOf(data, r.price, supplier))}${min}` };
            }),
    ];

    const useForCost = (priceId: string) => {
        setDraft({ source: { from: "price", priceId } });
        onCostTab?.();
    };
    // Its supplier prices, cheapest first: the list edge to edge in its card (adding one: a popup in the window).
    const supplierPricesCard = (
        <Box>
            <SectionCard
                label="Supplier prices"
                // Where its "Supplier prices to check" pill brings you (nothing in it: the card itself flashes).
                fix={<FixHere attached token={pricesFixToken}>{null}</FixHere>}
                // Adding one: beside its heading (as Name tags' link to its settings), its form a popup in the window.
                aside={
                    showAdd ? (
                        <button type="button" className="link-button inline-flex items-center gap-1" onClick={() => setAdding(true)}>
                            <Plus className="size-3.5" /> Add supplier price
                        </button>
                    ) : undefined
                }
                bodyClassName=""
            >
                {ranked.length === 0 ? (
                    <p className="m-0 p-3 text-muted">No supplier prices yet. The first one added sets the cost.</p>
                ) : (
                    <table className="list is-fixed">
                        <colgroup>
                            <col />
                            <col className="w-32" />
                            <col className="w-32" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Supplier</th>
                                <th className="text-right">Price each</th>
                                <th className="text-right">Checked</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...ranked].sort((a, b) => Number(a.price.minQty > 1) - Number(b.price.minQty > 1)).map(({ price, real }) => {
                                const chosen = source.from === "price" && source.priceId === price.id;
                                return (
                                    <PriceRow
                                        key={price.id}
                                        data={data}
                                        price={price}
                                        supplier={suppliers.get(price.supplierUuid)}
                                        setsCost={cost?.pick.price.id === price.id}
                                        // Due a check or out of date while its cost follows these prices: amber (Needs attention says so).
                                        due={data.costFollows.includes(item.uuid)}
                                        // What it really costs, when that isn't what it's quoted at (tax): on hover, and under its price opened.
                                        real={hasAmount(price) && Math.abs(toNumber(quotedOf(data, price, suppliers.get(price.supplierUuid))) - real) >= 0.005 ? real : null}
                                        open={openId === price.id}
                                        onToggle={() => setOpenId((id) => (id === price.id ? null : price.id))}
                                        // Its cost to follow it: a change to its cost (a draft), shown on its Costing tab to save.
                                        onUseForCost={can(data, "costs.edit") && !chosen && hasAmount(price) ? async () => useForCost(price.id) : undefined}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </SectionCard>
            {can(data, "prices.add") && priceDraft ? <AddPrice data={data} item={item} draft={priceDraft} onDraft={onPriceDraft} onDone={() => setAdding(false)} /> : null}
        </Box>
    );

    // What it's costed from: a kit's parts (it isn't bought as one), else its supplier prices.
    if (show === "sources") {
        return source.from === "parts" ? <KitParts data={data} item={item} /> : supplierPricesCard;
    }

    // Its tab's height (RecordWindow), so what it makes per unit (ProfitBox) sits at the bottom of the window when there's room, else at the end.
    return (
        <div className="flex flex-1 flex-col">
            {/* Where the cost comes from (a button each, as its Pricing card's ways of pricing) and why it's what it is, then the cost;
                with labour, what it's made of; the kits it's in; its warning along the bottom. */}
            <Box>
                <SectionCard
                    label="Cost"
                    aside="What one costs you."
                    // Being changed: tinted, as a pricing draft is, with its save bar along the bottom.
                    bodyClassName={clsx("space-y-3.5 p-3", costDraft && "bg-brand-500/5")}
                    footer={
                        costDraft ? (
                            <div className="border-t border-brand-500/30 bg-brand-500/5 px-3 py-2">
                                <CostSave data={data} item={item} draft={costDraft} typedBad={typedBad} onDone={() => onCostDraft(null)} />
                            </div>
                        ) : undefined
                    }
                    // Being changed, its save bar says what happens to its cost (not its Cost to update too).
                    fix={
                        costFix && !costDraft ? (
                            <FixHere attached token={costFixToken}>
                                {costFix}
                            </FixHere>
                        ) : undefined
                    }
                >
                    {/* A draft: saved by its save bar, so its fields don't say they're saved. */}
                    <DraftFields onInvalid={setTypedBad}>
                        <div className="space-y-1.5">
                            <ChoiceField<CostFrom>
                                label="Cost comes from"
                                hideLabel
                                value={fromOf(shown.source)}
                                options={COST_FROM}
                                disabled={!can(data, "costs.edit")}
                                // A supplier price: the one it follows if it does, else the cheapest to start with (another is chosen beside the cost).
                                onSave={async (from) => {
                                    if (from === "supplier") setDraft({ source: fromOf(source) === "supplier" ? source : { from: "cheapest" } });
                                    else setDraft({ source: { from } });
                                }}
                            />
                            {/* Following a supplier price, it's said beside which (below). */}
                            {following ? null : <CostExplanation data={view} item={item} source={shown.source} cost={shown.cost} />}
                        </div>
                        {/* Following a supplier price: which, with the choice above it, and beside it why it's what it is. */}
                        {following ? (
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <span className="field-label block">Which price</span>
                                    <SyncSelect
                                        label="Which supplier price its cost follows"
                                        options={whichOptions}
                                        value={shown.source.from === "price" ? shown.source.priceId : CHEAPEST}
                                        disabled={!can(data, "costs.edit")}
                                        onChange={which}
                                    />
                                </div>
                                {/* Level with the dropdown (under its label's height). */}
                                <div className="sm:pt-6">
                                    <CostExplanation data={view} item={item} source={shown.source} cost={shown.cost} />
                                </div>
                            </div>
                        ) : null}
                        {/* As its Pricing card: the part's on the left, its total (worked out) on the right, whichever way it's costed. */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <CostField
                                data={view}
                                item={item}
                                focusToken={costFocus}
                                typed={costDraft?.typed}
                                onTyped={(amount, quote) => setDraft({ source: { from: "typed" }, typed: { amount, quote } })}
                            />
                            <TotalCostField data={data} item={item} target={draftedCost} />
                        </div>
                    </DraftFields>
                    <PartOfKits data={data} item={item} />
                </SectionCard>
            </Box>
            {/* At the bottom (of the window when there's room), as on its Pricing tab: what it makes per unit, its drafts as they'd make it. */}
            <ProfitBox data={data} item={item} draft={pricingDraft} costDraft={costDraft} />
        </div>
    );
}

/**
 * The cost: typed in (as quoted: with or without tax, saved as it's left, remembered exactly as
 * typed), or while it follows a supplier price, that price as quoted (locked). See data.ts
 * itemCostShown.
 */
function CostField(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        focusToken?: number;
        /** What's typed in its draft (none: as saved). */
        typed?: { amount: string; quote: TaxQuote };
        /** Typed, or read another way: into the draft (saved by its save bar). */
        onTyped: (amount: string, quote: TaxQuote) => void;
    }>,
) {
    const { data, item, focusToken } = props;
    const shown = props.typed ? { ...props.typed, from: null } : itemCostShown(data, item);
    const follows = shown.from !== null;
    // With labour, the amount is only the part's (its labour goes on top: TotalCostField, beside it).
    const withLabour = (data.itemPricing[item.uuid]?.labourMinutes ?? 0) > 0;
    let label = "Amount";
    if (withLabour) label = shown.from === "its parts" ? "Parts' cost" : "Part cost";
    return (
        <QuotedAmountField
            label={label}
            amount={shown.amount}
            quote={shown.quote}
            rates={data.taxRates}
            disabled={!can(data, "costs.edit") || follows}
            quoteLocked={follows}
            validate={costCheck("Cost")}
            // Its pricing needs a cost it hasn't got (No cost, in Needs attention at the top of the window).
            warning={missingCost(partItem(data, item), data.itemPricing[item.uuid])}
            focusToken={focusToken}
            live
            onSave={async (typed) => props.onTyped(typed, shown.quote)}
            // Read the amount shown the new way (e.g. it was really inc GST), and type it that way from now on.
            onQuote={async (quote) => props.onTyped(shown.amount, quote)}
        />
    );
}

/**
 * Saving the Cost card's draft, as its Pricing card's (and laid out the same): what happens to its cost on the left, its buttons
 * on the right. Its cost moving: Save & update (ServiceM8's cost too, in the same go), Save only (it shows as a
 * cost to update; typed in, the typed figure waits as it), Cancel. Nothing moving it: Save, Cancel.
 */
function CostSave(
    props: Readonly<{ data: PriceListData; item: MaterialRow; draft: CostDraft; /** What's typed above can't be taken (the draft has what it had before). */ typedBad: boolean; onDone: () => void }>,
) {
    const { data, item, draft, onDone, typedBad } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const saved = itemSupplierPrices(data, item.uuid).source;
    const sourceMoves = JSON.stringify(saved) !== JSON.stringify(draft.source);
    const next = costWithLabour(data, item, draftPartCost(data, item, draft));
    const moves = !samePrice(next, item.cost);
    const typed = draft.source.from === "typed";
    const save = async (update: boolean) => {
        setBusy(true);
        setError(null);
        try {
            // Where it comes from first (a typed cost needs it typed), then its cost: as typed (written, or saved only: waiting as a
            // cost to update), or (asked) to what it now gives.
            if (sourceMoves) await saver.setCostSource(item.uuid, draft.source);
            if (typed && draft.typed) await saver.saveTypedCost(item.uuid, draft.typed.amount, draft.typed.quote, next, update);
            else if (update) await saver.updateCost(item.uuid);
            onDone();
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };
    return (
        <DraftBar
            // Just what happens to its cost, as its Pricing card's says its price (where it comes from is shown above).
            message={
                <>
                    {moves ? <ChangeLines cost={{ from: item.cost, to: next }} /> : "The cost doesn't change."}
                    {typedBad ? <span className="block text-xs text-danger">Put right what&apos;s marked above to save.</span> : null}
                </>
            }
            busy={busy}
            error={error}
            actions={
                <>
                    {moves ? (
                        <>
                            <button type="button" className="btn-primary" disabled={busy || typedBad} title={`Save it, and change its cost in ServiceM8 to ${fmt.money(next)}`} onClick={() => void save(true)}>
                                Save &amp; update
                            </button>
                            <button type="button" className="btn-secondary" disabled={busy || typedBad} title="Save it only: its cost in ServiceM8 stays as it is, and shows as a cost to update until it's updated" onClick={() => void save(false)}>
                                Save only
                            </button>
                        </>
                    ) : (
                        <button type="button" className="btn-primary" disabled={busy || typedBad} onClick={() => void save(false)}>
                            Save
                        </button>
                    )}
                    <button type="button" className="btn-secondary" disabled={busy} onClick={onDone}>
                        Cancel
                    </button>
                </>
            }
        />
    );
}

/** Its labour time's link to where the labour cost is set (Price List settings). */
function LabourCostLink({ data, children }: Readonly<{ data: PriceListData; children?: ReactNode }>) {
    const fmt = useMoney();
    return (
        <Link href={`/t/${meta.id}/settings#labour`} title="Change the labour cost (Price List settings)">
            {children ?? `${fmt.money(toNumber(data.settings.labourCost))} an hour`}
        </Link>
    );
}

/** Its cost once updated: the draft's (if it moves it), else its cost to update; none: as ServiceM8 has it. */
const costOnceUpdated = (data: PriceListData, item: MaterialRow, drafted: string | undefined) =>
    drafted === undefined ? data.costUpdates[item.uuid] : samePrice(drafted, item.cost) ? undefined : drafted;

/**
 * Beside the part cost, as its Pricing card's Total price is beside its fields: its total cost, worked out (never typed: grey), in
 * the item's terms (as ServiceM8 keeps it): the part's with its labour as it costs now (being edited: the draft's; with a cost to
 * update: what it'll be). Under it what its labour adds (its time at the labour cost, a link to where that's set), or where to set
 * the labour cost, or "No labour."
 */
function TotalCostField({ data, item, target: drafted }: Readonly<{ data: PriceListData; item: MaterialRow; target?: string }>) {
    const fmt = useMoney();
    const { uuid, rate } = itemTax(data, item);
    const minutes = data.itemPricing[item.uuid]?.labourMinutes ?? 0;
    const total = costOnceUpdated(data, item, drafted) ?? item.cost;
    let hint: ReactNode = "No labour.";
    if (minutes > 0 && labourCostOf(data, item.uuid) > 0) {
        hint = (
            <>
                {/* Worded as its Pricing tab's Labour hint: what its labour adds (in its terms), its time, at the labour cost (its link). */}
                +{fmt.money(costWithLabour(data, item, "0"))} {basisLabel(item.price_includes_taxes, rate)}: {duration(minutes)} at <LabourCostLink data={data} />
            </>
        );
    } else if (minutes > 0) {
        hint = (
            <>
                Its labour ({duration(minutes)}) isn&apos;t in it: <LabourCostLink data={data}>set the labour cost</LabourCostLink>.
            </>
        );
    }
    return (
        <QuotedAmountField
            label="Total cost"
            amount={toCents(toNumber(total))}
            quote={{ rateUuid: uuid, includesTax: item.price_includes_taxes }}
            rates={data.taxRates}
            amountLocked
            quoteLocked
            hint={hint}
            // Just its hint (its total the other way is in what it makes, below), so it fits on a line.
            quiet
            onSave={() => Promise.resolve()}
            onQuote={() => Promise.resolve()}
        />
    );
}

/** Why the cost is what it is, briefly: the price it follows and why that one (a cheaper one it isn't). */
function CostExplanation(props: Readonly<{ data: PriceListData; item: MaterialRow; source: CostSource; cost: CostPick | null }>) {
    const { data, item, source, cost } = props;
    const fmt = useMoney();
    const { rate, tax } = itemTax(data, item);
    const nameOf = (uuid: string) => data.suppliers.find((s) => s.uuid === uuid)?.name ?? "A supplier";
    const inBasis = (r: RankedPrice) => `${fmt.money(itemCost(r.real, item, tax))} ${basisLabel(item.price_includes_taxes, rate)}`;

    if (source.from === "typed") return <p className="text-(length:--label-text) text-muted">Typed in: what you pay for one.</p>;
    // A kit: its parts' Total, below, says it.
    if (source.from === "parts") return null;
    if (!cost) {
        const listed = data.supplierPrices.some((p) => p.materialUuid === item.uuid);
        return (
            <p className="text-(length:--label-text) text-muted">
                {listed ? "None of its supplier prices has an amount yet: the cost stays as it is until one does." : "Add a supplier price below and the cost will follow it."}
            </p>
        );
    }

    const { pick, cheaper } = cost;
    const bulk = pick.price.minQty > 1 ? ` (from ${fmt.number(pick.price.minQty)})` : "";
    const why: Record<CostPick["reason"], string> = {
        chosen: "",
        cheapest: " It's the cheapest.",
        bulkOnly: " Every price has a minimum, so it's the cheapest bulk price.",
        chosenMissing: " The chosen price is gone, so it's the cheapest.",
    };
    // Short: the amount's in the box beside it, and a price to check says so in Needs attention (and its row, below).
    const note = cheaper && cost.reason === "chosen" ? ` ${nameOf(cheaper.price.supplierUuid)} is cheaper, at ${inBasis(cheaper)}.` : "";

    return (
        <p className="text-(length:--label-text) text-muted">
            From {nameOf(pick.price.supplierUuid)}
            {bulk}.{why[cost.reason]}
            {note}
        </p>
    );
}

/** One supplier's price: a row to compare it by, which opens to its details underneath. */
function PriceRow(
    props: Readonly<{
        data: PriceListData;
        price: SupplierPriceRow;
        supplier: SupplierRow | undefined;
        setsCost: boolean;
        /** Its cost follows these prices: one due a check or out of date is amber (it needs attention). */
        due: boolean;
        /** What it really costs, before tax, when that isn't what it's quoted at (null: it's the same). */
        real: number | null;
        open: boolean;
        onToggle: () => void;
        /** Make the cost follow this supplier (not offered when it already does, or for viewers). */
        onUseForCost?: () => Promise<unknown>;
    }>,
) {
    const { data, price, supplier } = props;
    const fmt = useMoney();
    const { quote } = priceQuote(data, price, supplier);
    const age = ageOf(price.checkedAt, data.settings);
    const Chevron = props.open ? ChevronDown : ChevronRight;
    const windows = usePriceListWindows();
    // Opened: its details scrolled into view, all of them (as little as it takes, once they're drawn).
    const details = useRef<HTMLTableRowElement>(null);
    useEffect(() => {
        if (!props.open) return;
        const frame = requestAnimationFrame(() => details.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }));
        return () => cancelAnimationFrame(frame);
    }, [props.open]);
    return (
        <>
            <tr className={clsx("cursor-pointer", props.due && hasAmount(price) && age !== "current" ? "bg-warning-soft" : "hover:bg-surface-2", props.open && "bg-surface-2")} onClick={props.onToggle}>
                <td>
                    <span className="flex min-w-0 items-center gap-2">
                        <button type="button" className="inline-flex shrink-0 text-muted" aria-expanded={props.open} aria-label={`${supplier?.name ?? "Supplier"}: details`}>
                            <Chevron className="size-4" />
                        </button>
                        <SyncBadge synced={supplier?.synced ?? true} />
                        {/* One line: its badges kept whole, its name cut short to fit them (in full on hover). */}
                        <span className="flex min-w-0 flex-1 items-center gap-2 [&>.badge]:shrink-0">
                            {supplier ? (
                                // Opens the supplier (its window), not this row's details.
                                <button
                                    type="button"
                                    className="link-button min-w-0 truncate text-left font-medium"
                                    title={`Open the supplier: ${supplier.name}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        windows.openSupplier(supplier.uuid);
                                    }}
                                >
                                    {supplier.name}
                                </button>
                            ) : (
                                <span className="min-w-0 truncate font-medium">Unknown supplier</span>
                            )}
                            {props.setsCost ? <Badge tone="green">Sets the cost</Badge> : null}
                            {price.minQty > 1 ? <Badge tone="blue">From {fmt.number(price.minQty)}</Badge> : null}
                        </span>
                        {/* Their page for it (its link), at the cell's end so the links line up: opened from its line, not this row's details. */}
                        {price.url ? (
                            <a
                                href={price.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex shrink-0 items-center rounded p-0.5 text-muted hover:text-brand-ink"
                                title={`Open their page: ${price.url}`}
                                aria-label="Open their page"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ExternalLink className="size-3.5" />
                            </a>
                        ) : null}
                    </span>
                </td>
                <td className="text-right whitespace-nowrap" title={props.real === null ? undefined : costsYou(props.data, props.real, fmt)}>
                    {hasAmount(price) ? (
                        <>
                            {fmt.money(quotedOf(data, price, supplier))} <span className="text-xs text-muted">{quoteLabel(quote, data.taxRates)}</span>
                        </>
                    ) : (
                        <span className="text-muted">No price</span>
                    )}
                </td>
                <td className="text-right">
                    {hasAmount(price) ? (
                        <Badge tone={AGE[age].tone} title={`Checked ${timeAgo(price.checkedAt)}`}>
                            {AGE[age].label}
                        </Badge>
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                </td>
            </tr>
            {props.open ? (
                <tr ref={details}>
                    <td colSpan={3} className="bg-surface-2/50">
                        <PriceDetails data={data} price={price} supplier={supplier} real={props.real} onUseForCost={props.onUseForCost} />
                    </td>
                </tr>
            ) : null}
        </>
    );
}

/** An open price's details: its fields, Checked, History, Use for the cost and Remove. */
function PriceDetails(
    props: Readonly<{
        data: PriceListData;
        price: SupplierPriceRow;
        supplier: SupplierRow | undefined;
        /** What it really costs, before tax, when that isn't what it's quoted at: said under its price (null: the same). */
        real: number | null;
        onUseForCost?: () => Promise<unknown>;
    }>,
) {
    const { data, price, supplier } = props;
    const saver = usePriceListSaver();
    const [showHistory, setShowHistory] = useState(false);
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    // A big jump just saved (often a typo): pointed out, with Undo, until it's undone, changed again or closed.
    const [jump, setJump] = useState<{ text: string; from: number; to: number; before: Pick<SupplierPriceRow, "priceNet" | "checkedAt" | "changedAt"> } | null>(null);
    const fmt = useMoney();
    const { quote, tax } = priceQuote(data, price, supplier);
    const disabled = !can(data, "prices.update");

    const savePrice = async (typed: string) => {
        const before = { priceNet: price.priceNet, checkedAt: price.checkedAt, changedAt: price.changedAt };
        const from = toNumber(quotedPrice(before.priceNet, quote, tax));
        await saver.updatePrice(price, { priceNet: toStoredPrice(typed, quote, tax) });
        // Cleared (no amount now) is on purpose, not a jump.
        const text = typed.trim() === "" ? null : bigJump(from, toNumber(typed));
        setJump(text ? { text, from, to: toNumber(typed), before } : null);
    };
    // Back as it was: the amount, and when it was last checked and changed (not "checked now").
    const undo = ({ priceNet, checkedAt, changedAt }: Pick<SupplierPriceRow, "priceNet" | "checkedAt" | "changedAt">) => {
        setError(null);
        saver
            .updatePrice(price, { priceNet, restore: { checkedAt, changedAt } })
            .then(() => setJump(null))
            .catch(setError);
    };
    // Another quote: the amount shown, read that way (e.g. it was really inc GST, or the item is GST-free).
    const saveQuote = (next: TaxQuote) => {
        const shown = quotedOf(data, price, supplier);
        // Its stored price changes with it, so an Undo from before would no longer match.
        setJump(null);
        return saver.updatePrice(price, { quote: next, ...(shown ? { priceNet: toStoredPrice(shown, next, quoteRate(data, next).tax) } : {}) });
    };

    return (
        <div className="space-y-3 py-1">
            {/* Compact: its price, minimum and code on one row, its link under them; no lines under them (its row says when it was
                checked, and a bulk price's minimum), but a big change just made (with Undo). */}
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1fr)]">
                <QuotedAmountField
                    label="Price each"
                    amount={quotedOf(data, price, supplier)}
                    quote={quote}
                    rates={data.taxRates}
                    placeholder="Not known yet"
                    disabled={disabled}
                    validate={amountCheck}
                    // Nothing under it (compact), but what it really costs when that isn't its price, or a big change just made.
                    quiet
                    hint={props.real === null ? undefined : `${costsYou(props.data, props.real, fmt)}.`}
                    // A big change just saved: the field is highlighted and says so under it, with Undo (no need to look elsewhere).
                    warning={jump !== null}
                    note={
                        jump ? (
                            <span className="text-warning-ink">
                                <TriangleAlert className="mr-1 inline size-3.5 align-[-2px]" />
                                {jump.text} ({fmt.money(jump.from)} → {fmt.money(jump.to)}). Typo?{" "}
                                <button type="button" className="cursor-pointer font-semibold underline underline-offset-2 hover:no-underline" onClick={() => undo(jump.before)}>
                                    Undo
                                </button>
                                {" · "}
                                {/* It's right: nothing to undo, so the note goes. */}
                                <button type="button" className="cursor-pointer font-semibold underline underline-offset-2 hover:no-underline" onClick={() => setJump(null)}>
                                    Keep
                                </button>
                            </span>
                        ) : undefined
                    }
                    onSave={savePrice}
                    onQuote={saveQuote}
                />
                <TextField
                    label="Minimum to order"
                    value={String(price.minQty)}
                    inputMode="decimal"
                    disabled={disabled}
                    validate={qtyCheck}
                    onSave={(v) => saver.updatePrice(price, { minQty: Number(v) })}
                />
                <TextField label="Their code" value={price.supplierCode} disabled={disabled} onSave={(supplierCode) => saver.updatePrice(price, { supplierCode })} />
            </div>
            <TextField
                label="Link"
                value={price.url}
                placeholder="https://"
                disabled={disabled}
                suffix={
                    price.url ? (
                        <a href={price.url} target="_blank" rel="noreferrer" className="pointer-events-auto inline-flex items-center gap-1 text-brand-ink" aria-label="Open the link">
                            <ExternalLink className="size-3.5" />
                        </a>
                    ) : undefined
                }
                onSave={(url) => saver.updatePrice(price, { url })}
            />
            <div className="flex flex-wrap items-center gap-2">
                {disabled ? null : (
                    <button type="button" className="btn-secondary px-2.5 py-1 text-xs" title="Still the same price: mark it checked today" onClick={() => void saver.updatePrice(price, { checked: true }).catch(setError)}>
                        <CircleCheck className="size-3.5" /> Checked
                    </button>
                )}
                <button type="button" className="btn-secondary px-2.5 py-1 text-xs" aria-expanded={showHistory} onClick={() => setShowHistory((v) => !v)}>
                    <History className="size-3.5" /> History
                </button>
                {props.onUseForCost ? (
                    <button
                        type="button"
                        className="btn-secondary px-2.5 py-1 text-xs"
                        title="The item's cost follows this price, even when another is cheaper or it's a bulk price"
                        onClick={() => void props.onUseForCost?.().catch(setError)}
                    >
                        <Target className="size-3.5" /> Use for the cost
                    </button>
                ) : null}
                {disabled ? null : (
                    <button
                        type="button"
                        className="btn-secondary ml-auto px-2.5 py-1 text-xs hover:text-danger"
                        onClick={async () => {
                            const yes = await confirm({
                                title: "Remove this supplier price?",
                                subject: supplier?.name,
                                message: "The price goes, with its history. If the item's cost follows it, it follows the next one.",
                                confirmLabel: "Remove price",
                                tone: "danger",
                            });
                            if (yes) saver.removePrice(price).catch(setError);
                        }}
                    >
                        <Trash2 className="size-3.5" /> Remove
                    </button>
                )}
            </div>
            <ErrorNotice error={error} />
            {showHistory ? <PriceHistory data={data} price={price} supplier={supplier} /> : null}
        </div>
    );
}

/** A price's changes, newest first; mistakes can be marked so they're left out of trends. */
function PriceHistory({ data, price, supplier }: Readonly<{ data: PriceListData; price: SupplierPriceRow; supplier: SupplierRow | undefined }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const { data: history, error } = usePriceHistory(price.id);
    const { quote, tax } = priceQuote(data, price, supplier);
    if (error) return <ErrorNotice error={error} />;
    if (!history) return <p className="text-xs text-muted">Loading…</p>;
    return (
        <table className="list text-xs">
            <thead>
                <tr>
                    <th>When</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Minimum</th>
                    <th>By</th>
                    <th />
                </tr>
            </thead>
            <tbody>
                {history.map((h) => (
                    <tr key={h.id} className={h.ignored ? "text-muted line-through" : undefined}>
                        <td>{new Date(h.changedAt).toLocaleDateString()}</td>
                        <td className="text-right">{fmt.money(quotedPrice(h.priceNet, quote, tax))}</td>
                        <td className="text-right">{fmt.number(h.minQty)}</td>
                        <td>{h.changedBy ?? "-"}</td>
                        <td className="text-right">
                            {can(data, "prices.update") ? (
                                <button type="button" className="link-button text-xs" onClick={() => void saver.setHistoryIgnored(price.id, h.id, !h.ignored)}>
                                    {h.ignored ? "Count it" : "A mistake"}
                                </button>
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/**
 * The supplier for a new price: one of the active suppliers, or a new one added by name right here
 * (synced with ServiceM8 when it can be, as on the Suppliers page), which is then chosen.
 */
function SupplierPicker({ data, value, onChange }: Readonly<{ data: PriceListData; value: string; onChange: (uuid: string) => void }>) {
    const saver = usePriceListSaver();
    const active = data.suppliers.filter((s) => s.active);
    // With no suppliers yet, the only way is a new one.
    const [naming, setNaming] = useState(active.length === 0);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const canChoose = active.length > 0;
    const toggleLabel = naming ? "Choose one instead" : "New supplier";

    const create = () => {
        const trimmed = name.trim();
        if (!trimmed || busy) return;
        setBusy(true);
        setError(null);
        saver
            .createSupplier({ name: trimmed })
            .then((s) => {
                onChange(s.uuid);
                setNaming(false);
                setName("");
            })
            .catch((err: Error) => setError(err.message))
            .finally(() => setBusy(false));
    };

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
                <span className="field-label">{naming ? "New supplier's name" : "Supplier"}</span>
                {canChoose ? (
                    <button type="button" className="link-button inline-flex items-center gap-1 text-xs" onClick={() => setNaming((v) => !v)}>
                        {naming ? null : <Plus className="size-3.5" />} {toggleLabel}
                    </button>
                ) : null}
            </div>
            {naming ? (
                <div className="flex gap-2">
                    <input
                        className="input"
                        value={name}
                        aria-label="New supplier's name"
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            // Enter adds the supplier, not the price.
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            create();
                        }}
                    />
                    <button type="button" className="btn-secondary shrink-0" disabled={busy || !name.trim()} onClick={create}>
                        Add
                    </button>
                </div>
            ) : (
                <SyncSelect
                    label="Supplier"
                    placeholder="Choose a supplier"
                    value={value}
                    onChange={onChange}
                    options={active.map((s) => ({ value: s.uuid, label: s.name, synced: s.synced }))}
                />
            )}
            {error ? <p className="text-(length:--label-text) text-danger">{error}</p> : null}
        </div>
    );
}

/** Adding a price: the supplier, their price as they quote it, a minimum, and optionally their code and a link to the part. Closes once added (or cancelled). */
function AddPrice({ data, item, draft, onDraft, onDone }: Readonly<{ data: PriceListData; item: MaterialRow; draft: PriceDraft; onDraft: (draft: PriceDraft) => void; onDone: () => void }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const { supplierUuid, price, minQty, code, url, picked } = draft;
    const set = (patch: Partial<PriceDraft>) => onDraft({ ...draft, ...patch });
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const supplier = data.suppliers.find((s) => s.uuid === supplierUuid);
    const quote = picked ?? usualQuote(supplier?.details ?? { pricesIncludeTax: false, purchaseTaxRateUuid: "" });
    const { tax } = quoteRate(data, quote);
    // Something typed that can't be taken (its field says why) holds it back too: the draft still has what it had before.
    const [typedBad, setTypedBad] = useState(false);
    const valid = supplierUuid && !typedBad && amountCheck(price) === null && qtyCheck(minQty) === null;
    // Its fields change the draft as they're typed in (live): nothing to wait for.
    const draftSet = (patch: Partial<PriceDraft>) => {
        set(patch);
        return Promise.resolve();
    };
    // Under the price (after the amount the other way): what it really costs, when that isn't what's typed (tax on purchases).
    const real = toNumber(price) > 0 ? realCost(storedPrice(price, quote, tax), tax, data.settings) : null;
    const realCostHint = real !== null && Math.abs(toNumber(price) - real) >= 0.005 ? `Costs you ${fmt.money(real)}.` : undefined;

    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!valid) return;
        setBusy(true);
        setError(null);
        saver
            .addPrice({ materialUuid: item.uuid, supplierUuid, minQty: Number(minQty.trim()), priceNet: toStoredPrice(price.trim(), quote, tax), quote, supplierCode: code.trim(), url: url.trim() })
            .then(onDone)
            .catch((err: Error) => {
                setError(err);
                setBusy(false);
            });
    };

    return (
        // A popup in the window (as a kit's Add item, and Duplicate): its supplier, then the same fields as a price's row opened
        // (PriceDetails). A draft: nothing's saved until Add price; closing it (Cancel, Escape, outside it) drops it.
        <WindowPopup title="New supplier price" onClose={onDone}>
            <form className="space-y-4" onSubmit={submit}>
                <DraftFields onInvalid={setTypedBad}>
                    <SupplierPicker
                        data={data}
                        value={supplierUuid}
                        // How it's quoted: as the supplier usually quotes, until changed here.
                        onChange={(uuid) => set({ supplierUuid: uuid, picked: null })}
                    />
                    {/* What it costs: the price (wide, its tax inside it), its minimum beside it. */}
                    <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_9rem]">
                        <QuotedAmountField
                            label="Price each"
                            amount={price}
                            quote={quote}
                            rates={data.taxRates}
                            placeholder="Not known yet"
                            validate={amountCheck}
                            hint={realCostHint}
                            live
                            onSave={(typed) => draftSet({ price: typed })}
                            onQuote={(q) => draftSet({ picked: q })}
                        />
                        <TextField
                            label="Minimum to order"
                            value={minQty}
                            inputMode="decimal"
                            validate={qtyCheck}
                            hint={Number(minQty) > 1 ? "A bulk price." : "1 = no minimum."}
                            live
                            onSave={(v) => draftSet({ minQty: v })}
                        />
                    </div>
                    {/* The rest, optional: theirs to find it by. */}
                    <div className="grid gap-4 sm:grid-cols-2">
                        <TextField label="Their code (optional)" value={code} live onSave={(v) => draftSet({ code: v })} />
                        <TextField label="Link (optional)" value={url} placeholder="https://" live onSave={(v) => draftSet({ url: v })} />
                    </div>
                </DraftFields>
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={onDone}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !valid}>
                        <Plus className="size-4" /> Add price
                    </button>
                </div>
            </form>
        </WindowPopup>
    );
}

/**
 * What a supplier price really costs (supplierPricing.ts realCost), in its terms: before tax when the tax on purchases is claimed
 * back, with it when it isn't (then it's a cost too).
 */
const costsYou = (data: PriceListData, real: number, fmt: ReturnType<typeof useMoney>) =>
    `Costs you ${fmt.money(real)} ${data.settings.purchaseTaxClaimed ? "before tax" : "with its tax (not claimed back)"}`;

/** Why its cost is to update: what its parts or supplier price give, what its labour costs now, or both. */
function costUpdateWhy(data: PriceListData, item: MaterialRow, target: string, fmt: ReturnType<typeof useMoney>) {
    // A kit: its parts; following: its supplier price; typed in (saved only, waiting): what was typed.
    let from = "its supplier price";
    if (item.uuid in data.kitParts) from = "its parts";
    else if (!data.costFollows.includes(item.uuid)) from = "the cost typed";
    // Its part's cost the same, with its labour now: only its labour moved.
    const partMoved = !samePrice(target, costWithLabour(data, item, partItem(data, item).cost));
    if (!labourChanged(data, item.uuid)) return `From ${from}`;
    if (partMoved) return `From ${from} and its labour`;
    const minutes = data.itemPricing[item.uuid]?.labourMinutes ?? 0;
    if (labourCostOf(data, item.uuid) <= 0) return "Without labour now";
    return `For its labour (${duration(minutes)} at ${fmt.money(toNumber(data.settings.labourCost))}/hr)`;
}

/**
 * Its cost isn't what its supplier price or its parts give now, or its labour costs another amount (supplierPrices.ts costUpdates):
 * a price added, changed or removed, another followed, tax on purchases, its labour time or the labour cost changed. Costs never
 * change by themselves, so ServiceM8 keeps the old one until someone updates it here. At the top of its window.
 */
export function CostUpdateNotice({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const target = data.costUpdates[item.uuid];
    if (target === undefined) return null;
    const kit = item.uuid in data.kitParts;
    const kitTerms = { amount: toNumber(item.cost), label: basisLabel(item.price_includes_taxes, itemTax(data, item).rate) };
    const now = kit ? kitTerms : shownCost(data, item);
    const next = kit ? { ...kitTerms, amount: toNumber(target) } : shownCost(data, { ...item, cost: target });
    const update = () => {
        setSaving(true);
        setError(null);
        saver
            .updateCost(item.uuid)
            .catch((err: Error) => setError(err))
            .finally(() => setSaving(false));
    };
    return (
        <Notice
            tone="warning"
            title="Cost to update:"
            actions={
                can(data, "costs.edit") ? (
                    <button type="button" className="btn-secondary" disabled={saving} onClick={update}>
                        <StableLabel labels={["Update cost", "Updating..."]} shown={saving ? "Updating..." : "Update cost"} />
                    </button>
                ) : null
            }
        >
            <span className="tabular-nums">
                {fmt.money(now.amount)} → <strong>{fmt.money(next.amount)}</strong> {next.label}
            </span>
            {/* Why, in a few words (its button says what to do; nothing changes in ServiceM8 until it's pressed). */}
            <span className="block">{costUpdateWhy(data, item, target, fmt)}.</span>
            {error ? <ErrorNotice error={error} /> : null}
        </Notice>
    );
}
